import React, { useState } from "react";
import { X, Search, User, Info } from "lucide-react";
import { DoctorDto, DoctorWithScheduleDto } from "@/src/web-api-client";
import { getDoctorDefaultImg } from "../../../../utils/defaultImage";
import { useBackToCloseModal } from "../../../../hooks/useBackToCloseModal";
import { useNavigate } from "react-router-dom";
import { normalizeText } from "../../../../utils/search";
import { useLanguage } from "../../../../i18n/LanguageContext";

type DoctorImageProps = {
  src?: string | null;
  alt: string;
  gender?: any;
};

function DoctorImage({ src, alt, gender }: DoctorImageProps) {
  const [isLandscape, setIsLandscape] = useState(false);

  return (
    <div
      className={`w-20 bg-green-100 rounded-xl rounded-tl-[35px] rounded-tr-[35px] overflow-hidden ${
        isLandscape ? "h-[100px]" : src ? "h-[110px]" : ""
      }`}
    >
      <img
        src={src || getDoctorDefaultImg(gender)}
        alt={alt}
        className="w-full h-full object-cover"
        onLoad={(e) => {
          const img = e.currentTarget;
          setIsLandscape(img.naturalWidth > img.naturalHeight);
        }}
        onError={(e) => {
          const img = e.currentTarget as HTMLImageElement;
          const fallback = getDoctorDefaultImg(gender);

          if (!img.src.includes(fallback)) {
            img.onerror = null;
            img.src = fallback;
          }
        }}
      />
    </div>
  );
}

//MOBILE
interface DoctorModalProps {
  doctors: DoctorWithScheduleDto[];
  setShowBottomSheet: (show: boolean) => void;
  showBottomSheet: boolean;
  handleDoctorSelect: (doctor: DoctorWithScheduleDto) => void;
  warning?: boolean;
}

const DoctorModal = ({
  doctors,
  setShowBottomSheet,
  showBottomSheet,
  handleDoctorSelect,
  warning = false,
}: DoctorModalProps) => {
  const [searchTerm, setSearchTerm] = useState("");

  const navigate = useNavigate();
  const { t } = useLanguage();

  const filteredDoctors = doctors.filter((doctor) => {
    if (!searchTerm) return true;

    const target = normalizeText(
      `${doctor.name ?? ""} ${doctor.formattedTitle ?? ""}`,
    ).toLowerCase();
    const q = normalizeText(searchTerm).toLowerCase();

    return target.includes(q);
  });

  useBackToCloseModal(
    showBottomSheet,
    () => setShowBottomSheet(false),
    "doctor-modal",
  );

  const handleViewDetail = (doctor: any) => {
    if (!doctor.doctorId && !doctor.id) return;

    const params = new URLSearchParams({
      doctorId: doctor.doctorId ?? doctor.id,
    });

    navigate(`/profile?${params.toString()}`);
  };

  return (
    <div>
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center space-x-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Tìm kiếm bác sĩ..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="text-base w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>
        </div>

        {warning && (
          <div className="mt-4 px-4 py-2 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-gray-700 leading-relaxed">
              <span className="font-semibold mr-2">Lưu ý:</span>
              Lịch khám sẽ hiển thị theo lịch của bác sĩ và các lựa chọn
              ngày/giờ trước đó sẽ được đặt lại.
            </p>
          </div>
        )}
      </div>

      <div className="overflow-y-auto max-h-[62vh]">
        <div className="pb-16">
          {filteredDoctors.map((doctor: any, index: number) => {
            return (
              <div
                key={doctor.id ?? doctor.doctorId}
                className="bg-white border-b border-gray-200 p-4 hover:border-green-300 hover:shadow-md transition-all duration-200 cursor-pointer"
                onClick={() => {
                  handleDoctorSelect(doctor);
                  setShowBottomSheet(false);
                }}
              >
                <div className="relative flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <DoctorImage
                      src={doctor.image ?? doctor.originalUrl}
                      alt={doctor.name}
                      gender={doctor.gender}
                    />
                  </div>

                  <div className="flex-1 min-w-0">
                    <span className="text-green-500 text-base font-medium mb-2 leading-tight">
                      {doctor.name}
                    </span>

                    <div className="space-y-2 mt-2">
                      <div className="flex items-start">
                        {doctor.formattedTitle &&
                        doctor.formattedTitle.trim() !== "" ? (
                          <span className="text-gray-700 text-sm font-medium">
                            {t("chucdanh")}: {doctor.formattedTitle}
                          </span>
                        ) : (
                          <>
                            <span className="text-gray-700 text-sm font-medium">
                              {t("chucdanh")}:
                            </span>
                            <span className="text-gray-500 text-sm ml-2">
                              Đang cập nhật...
                            </span>
                          </>
                        )}
                      </div>

                      {doctor.specialty && doctor.specialty.trim() !== "" && (
                        <div className="flex items-start">
                          <span className="text-gray-700 text-sm font-medium">
                            {t("chuyenkhoa")}:
                          </span>
                          <span className="text-gray-600 text-sm ml-2">
                            {doctor.specialty}
                          </span>
                        </div>
                      )}

                      <div className="items-center">
                        <span className="text-gray-700 font-medium text-sm min-w-[80px]">
                          Lịch khám:
                        </span>
                        <span className="text-gray-600 text-sm ml-1">
                          {(doctor.daysThisWeek ?? []).join(", ")}
                        </span>
                      </div>

                      <div className="flex items-center">
                        <span className="text-sm">
                          Giá khám:{" "}
                          <span className="text-primary">
                            {(doctor.consultationFee ?? 1600000).toLocaleString(
                              "vi-VN",
                            )}
                            đ
                          </span>
                        </span>
                      </div>
                    </div>
                  </div>

                  <div
                    className="relative w-10 h-10 hover:bg-green-50 rounded-full"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowBottomSheet(false);

                      setTimeout(() => {
                        handleViewDetail(doctor);
                      }, 0);
                    }}
                  >
                    <Info className="absolute w-5 h-5 text-green-500 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {filteredDoctors.length === 0 && (
          <div className="p-8 text-center">
            <User className="w-10 h-10 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-base">
              Không tìm thấy bác sĩ phù hợp
            </p>
            <p className="text-gray-400 text-sm mt-1">
              Thử tìm kiếm với từ khóa khác
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DoctorModal;
